import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, Checkbox, Box, CircularProgress, Alert, TableContainer
} from '@mui/material';
import { AccessModule, AccessPermission, GrantAccessPayload, UserPermission } from '../../types/access-control.types';
import { getPermissionsForUser } from '../../services/access-control.service';
import { ModalIndices } from '../../common/i-doc/i-doc.registry';

interface GrantAccessModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (payloads: { toGrant: GrantAccessPayload[], toRevoke: number[] }) => void;
  editingUserId?: number | null;
}

type PermissionsState = {
  [key in AccessModule]?: AccessPermission[];
};

export const GrantAccessModal: React.FC<GrantAccessModalProps> = ({ open, onClose, onSave, editingUserId }) => {
  const [granteeId, setGranteeId] = useState('');
  const [permissions, setPermissions] = useState<PermissionsState>({});
  const [initialPermissions, setInitialPermissions] = useState<UserPermission[]>([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = editingUserId != null;

  useEffect(() => {
    if (isEditMode && open) {
      setGranteeId(String(editingUserId));
      const fetchUserPermissions = async () => {
        setLoading(true);
        setError(null);
        try {
          const userPerms = await getPermissionsForUser(editingUserId);
          setInitialPermissions(userPerms);
          const permsState: PermissionsState = {};
          let commonExpiresAt = '';
          userPerms.forEach(p => {
            permsState[p.moduleCode] = p.permissions;
            if (p.expiresAt && typeof p.expiresAt === 'string') {
              commonExpiresAt = p.expiresAt.substring(0, 16);
            }
          });
          setPermissions(permsState);
          setExpiresAt(commonExpiresAt);
        } catch (err) {
          console.error("Error processing user permissions:", err);
          setError('Не вдалося завантажити доступи користувача.');
        } finally {
          setLoading(false);
        }
      };
      fetchUserPermissions();
    } else {
      setGranteeId('');
      setPermissions({});
      setInitialPermissions([]);
      setExpiresAt('');
    }
  }, [editingUserId, isEditMode, open]);

  const handlePermissionChange = (module: AccessModule, permission: AccessPermission) => {
    const currentPermissions = permissions[module] || [];
    let newPermissions;

    const isBeingChecked = !currentPermissions.includes(permission);

    if (isBeingChecked) {
      // Add the permission
      newPermissions = [...currentPermissions, permission];
      // If 'write' is checked, automatically check 'read'
      if (permission === AccessPermission.WRITE && !newPermissions.includes(AccessPermission.READ)) {
        newPermissions.push(AccessPermission.READ);
      }
    } else {
      // Remove the permission
      newPermissions = currentPermissions.filter(p => p !== permission);
      // If 'read' is unchecked, automatically uncheck 'write'
      if (permission === AccessPermission.READ) {
        newPermissions = newPermissions.filter(p => p !== AccessPermission.WRITE);
      }
    }

    setPermissions({ ...permissions, [module]: newPermissions.length > 0 ? newPermissions : undefined });
  };

  const handleSubmit = () => {
    const finalGranteeId = Number(granteeId);
    if (isNaN(finalGranteeId)) {
      alert('Будь ласка, введіть коректний ID користувача.');
      return;
    }

    const toGrant: GrantAccessPayload[] = [];
    const toRevoke: number[] = [];
    const allModules = Object.values(AccessModule);

    allModules.forEach(module => {
      const initialModulePerm = initialPermissions.find(p => p.moduleCode === module);
      const currentModulePerms = permissions[module] || [];

      const initialSet = new Set(initialModulePerm?.permissions || []);
      const currentSet = new Set(currentModulePerms);

      // If the sets are different, something has changed
      if (initialSet.size !== currentSet.size || ![...initialSet].every(p => currentSet.has(p))) {
        // If there was an old permission, it needs to be revoked
        if (initialModulePerm) {
          toRevoke.push(initialModulePerm.id);
        }
        // If there are new permissions, they need to be granted
        if (currentModulePerms.length > 0) {
          toGrant.push({
            granteeId: finalGranteeId,
            moduleCode: module,
            permissions: currentModulePerms,
            expiresAt: expiresAt || null,
          });
        }
      }
    });

    const finalPayloads = { toGrant, toRevoke };

    if (finalPayloads.toGrant.length === 0 && finalPayloads.toRevoke.length === 0) {
      alert('Не було зроблено жодних змін.');
      return;
    }

    onSave(finalPayloads);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-i-doc={ModalIndices.GRANT_ACCESS}>
      <DialogTitle>{isEditMode ? `Редагування доступів для користувача ${editingUserId}` : 'Надання доступу'}</DialogTitle>
      <DialogContent>
        {loading ? <CircularProgress /> : error ? <Alert severity="error">{error}</Alert> : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <TextField
              label="ID Користувача"
              variant="outlined"
              value={granteeId}
              onChange={(e) => setGranteeId(e.target.value)}
              required
              type="number"
              autoFocus
              disabled={isEditMode}
            />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Модуль</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Читати (read)</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Записувати (write)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.values(AccessModule).map((module) => (
                    <TableRow key={module}>
                      <TableCell>{module}</TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={permissions[module]?.includes(AccessPermission.READ) || false}
                          onChange={() => handlePermissionChange(module, AccessPermission.READ)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={permissions[module]?.includes(AccessPermission.WRITE) || false}
                          onChange={() => handlePermissionChange(module, AccessPermission.WRITE)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TextField
              label="Дійсний до (опціонально)"
              type="datetime-local"
              variant="outlined"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Застосовується до всіх нових або змінених доступів"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Скасувати</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>Зберегти зміни</Button>
      </DialogActions>
    </Dialog>
  );
};
